import { WaitingAlert } from 'app/shared/models/waiting-alert.model';

const MINUTE_IN_MS = 60 * 1000;

/**
 * The anomalies the design prototype raises, used until the alerts endpoint exists.
 *
 * Only `AlertsService.fetchWaitingAlerts` reads this, so the endpoint replaces one method and this
 * file goes away with it. The wording, drivers and steps are the prototype's verbatim — they are
 * the design's own copy and the thing reviewers will be reading.
 *
 * `raisedAt` is relative to load rather than absolute so the fixture never reads as stale; nothing
 * renders the timestamp, it only orders the list.
 */
export function buildWaitingAlertsFixture(now: Date = new Date()): WaitingAlert[] {
  const minutesAgo = (minutes: number): Date => new Date(now.getTime() - minutes * MINUTE_IN_MS);

  return [
    {
      id: 'al-1',
      title: 'Service level — DC2',
      summary: '68% of the gap traces to receipt lead time on supplier VN-118 drifting 21 → 34 days.',
      delta: '−4.8 pp',
      severity: 'high',
      kind: 'Gradual',
      owningAgentId: 'service-anomaly',
      owningAgentName: 'Service Level Anomaly Detection',
      raisedAt: minutesAgo(25),
      drivers: [
        { label: 'Receipt lead time drift — supplier VN-118', share: '68%' },
        { label: 'Safety stock not re-planned at observed lead time', share: '21%' },
        { label: 'Demand variability above plan — 3 SKU families', share: '11%' }
      ],
      suggestedPath: {
        rootCause: 'Achieved fill at DC2 is 91.2% against a 96.0% target — 4.8pp below, tagged gradual. It crossed the 3pp threshold in week 9 and has widened since.',
        steps: [
          {
            order: 1,
            text: 'Re-plan safety stock on 1,412 SKULs at the observed 34-day lead time.',
            note: 'Fixes the cause — closes about 3.2pp of the gap once the plan refreshes.'
          },
          {
            order: 2,
            text: 'Raise a lead-time review with supplier VN-118 — the planning value is 13 days stale.',
            note: 'Stops it recurring. Needs a person; nothing changes until the value is agreed.'
          },
          {
            order: 3,
            text: 'Expedite the 14 SKULs already below reorder point.',
            note: 'Buys time while the re-plan takes effect — cost impact, so worth a decision.'
          }
        ]
      }
    },
    {
      id: 'al-2',
      title: 'Lead time drift — VN-118',
      summary: 'Receipt lead time has moved away from the planning value on every order since April.',
      delta: '+62%',
      severity: 'medium',
      kind: 'Gradual',
      owningAgentId: 'supply-performance-anomaly',
      owningAgentName: 'Supply Performance Anomaly Detection',
      raisedAt: minutesAgo(120),
      drivers: [
        { label: 'Port dwell on inbound lanes from VN', share: '54%' },
        { label: 'Supplier production lead time extended, not communicated', share: '31%' },
        { label: 'Order consolidation adding transit days', share: '15%' }
      ],
      suggestedPath: {
        rootCause:
          'Receipt lead time on supplier VN-118 is running at 34 days against a planning value of 21 — 62% above, tagged gradual. The last nine receipts are all late, so this is drift rather than a single miss.',
        steps: [
          {
            order: 1,
            text: 'Update the planning lead time on VN-118 to the observed 34 days.',
            note: 'Fixes the parameter the plan is wrong on — reversible, applies next cycle.'
          },
          {
            order: 2,
            text: 'Raise a lead-time review with the supplier before the next PO wave.',
            note: 'Confirms whether 34 days is the new normal. Needs a person.'
          },
          {
            order: 3,
            text: 'Re-check service level on the 1,412 SKULs sourced from VN-118.',
            note: 'This drift is the largest driver behind the DC2 fill-rate breach.'
          }
        ]
      }
    },
    {
      id: 'al-3',
      title: 'Days of supply — DC1',
      summary: 'Coverage jumped when a 40k-unit transfer landed against a demand plan already revised down.',
      delta: '+22%',
      severity: 'medium',
      kind: 'Sudden',
      owningAgentId: 'inventory-health-anomaly',
      owningAgentName: 'Inventory Health Anomaly Detection',
      raisedAt: minutesAgo(300),
      drivers: [
        { label: 'Demand plan revised down, replenishment not re-run', share: '61%' },
        { label: 'Minimum order quantity rounding on 74 SKULs', share: '27%' },
        { label: 'Early receipts pulled into the period', share: '12%' }
      ],
      suggestedPath: {
        rootCause:
          'Days of supply at DC1 is 47 days against a policy band of 32–38 — 22% above the ceiling, tagged sudden. 260 SKULs moved together, which points at a plan change rather than demand.',
        steps: [
          {
            order: 1,
            text: 'Re-run replenishment on the 260 SKULs against the revised demand plan.',
            note: 'Fixes the cause — brings coverage back inside the band without touching policy.'
          },
          {
            order: 2,
            text: 'Review MOQ on the 74 rounded SKULs with procurement.',
            note: 'Structural; needs a person. Worth doing only if the excess persists.'
          },
          {
            order: 3,
            text: 'Hold the next inbound wave for the 41 SKULs above 60 days.',
            note: 'Avoids adding to the excess — cost and supplier impact, so decide explicitly.'
          }
        ]
      }
    }
  ];
}
